import { makeAutoObservable, runInAction } from "mobx";
import type RootStore from "stores/RootStore";
import type { ServiceStoreType } from "../../../types/ServiceStoreTypes";
import { readFindTotal } from "shared/truncation";
import { findOrThrow, loadErrorMessage } from "shared/loadOrError";
import {
  isDevOpsConnectionValid,
  type DevOpsConnection,
  type DevOpsConnectionFormData,
  type DevOpsConnectionStatus,
  type DevOpsConnectionValidatePayload,
  type DevOpsConnectionValidateResponse,
  type DevOpsConnectionValidateResult,
} from "../devops.types";
import {
  dateValue,
  nextSort,
  sortRows,
  textValue,
  type SortDir,
  type SortValue,
} from "components/TableSort/tableSort";
import { THROW_ON_ERROR } from "shared/dialogSubmit";
import { isSessionEnded } from "stores/sessionGuard";

export type DevOpsConnectionStatusFilter = "all" | DevOpsConnectionStatus;

/** Page size for the connection read. `devops-connections` is a real paginated
 *  KnexService (unlike the five Azure proxies), so this cap is enforced — see
 *  {@link DevOpsConnectionsStore.isTruncated}. */
export const DEVOPS_CONNECTION_PAGE_SIZE = 200;

export type DevOpsConnectionSortKey =
  | "name"
  | "orgUrl"
  | "defaultProject"
  | "status"
  | "lastValidatedAt";

/** Sort keys that read best newest-first on first click. */
const DESC_FIRST: ReadonlySet<DevOpsConnectionSortKey> = new Set([
  "lastValidatedAt",
]);

/**
 * View store for the Azure DevOps Connections admin view.
 *
 * Rule: route ViewStores are plain classes with `makeAutoObservable` and do NOT
 * extend ViewStore. The connection list is the registered `devops-connections`
 * ServiceStore's entity cache — `connections` is a COMPUTED view over it (the
 * MobX rule keeps API entities in the ServiceStore, never duplicated here).
 *
 * SECRET HANDLING: the PAT is write-only. The form captures `pat` and submits
 * it on save, but the API never returns the token — `DevOpsConnection` carries
 * only a `hasPat` boolean, never the secret. The form's `pat` field is cleared
 * after every save/edit so it is never repopulated from a record.
 */
class DevOpsConnectionsStore {
  rootStore: RootStore;

  loading = false;
  error: string | null = null;
  /**
   * `total` from the last find envelope — how many rows the SERVER has, as
   * opposed to how many it sent. Null until a paginated response reports one.
   * A plain number, not an entity, so the "no entities in a ViewStore" rule
   * still holds. See ../findTotal.ts for why this is not read off
   * `ServiceStore.pagination.total`.
   */
  serverTotal: number | null = null;

  /**
   * Failure of a row action (currently: remove). Kept separate from `error`
   * (which is the load failure shown in the empty grid) so a failed delete is
   * announced even when the grid still has rows. Mirrors ClientInstancesStore's
   * `actionError` / `clearActionError()` pair.
   */
  actionError: string | null = null;

  // Header filter
  searchQuery = "";
  statusFilter: DevOpsConnectionStatusFilter = "all";

  // Column sort (applied client-side over the filtered rows, like the filters)
  sortKey: DevOpsConnectionSortKey = "name";
  sortDir: SortDir = "asc";

  // Create/edit form state (shared by the connection form dialog)
  editingConnectionId: string | null = null;
  formData: DevOpsConnectionFormData = {};
  saving = false;
  formError: string | null = null;

  // Per-connection "Test connection" state
  testingConnectionId: string | null = null;
  lastTestResult: DevOpsConnectionValidateResult | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, { rootStore: false });
  }

  /** The registered `devops-connections` ServiceStore (devops.stores.ts). */
  private get connectionsService(): ServiceStoreType | null {
    return ((this.rootStore.serviceStoreManager?.getStore("devops-connections") ?? this.rootStore.devopsConnectionsStore) as unknown as ServiceStoreType | null) ?? null;
  }

  /**
   * Connection list. Per the MobX rule, entities live in the ServiceStore cache
   * (populated by `find`), not in this ViewStore — this is a computed view.
   */
  get connections(): DevOpsConnection[] {
    return (this.connectionsService?.entities ?? []) as unknown as DevOpsConnection[];
  }

  // ---- Derived data -------------------------------------------------------

  connectionName(id?: string | null): string {
    if (!id) return "";
    return this.connections.find((c) => c.id === id)?.name || "";
  }

  connectionById(id?: string | null): DevOpsConnection | null {
    if (!id) return null;
    return this.connections.find((c) => c.id === id) || null;
  }

  get filteredConnections(): DevOpsConnection[] {
    let result = this.connections;

    if (this.statusFilter !== "all") {
      if (this.statusFilter === "valid") {
        // "Valid" matches the API's success status ("verified") too.
        result = result.filter((c) => isDevOpsConnectionValid(c.status));
      } else if (this.statusFilter === "invalid") {
        // "Invalid" covers both invalid + error.
        result = result.filter(
          (c) => c.status === "invalid" || c.status === "error"
        );
      } else {
        result = result.filter((c) => c.status === this.statusFilter);
      }
    }

    if (this.searchQuery.trim()) {
      const q = this.searchQuery.toLowerCase().trim();
      result = result.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) ||
          c.orgUrl?.toLowerCase().includes(q) ||
          c.defaultProject?.toLowerCase().includes(q)
      );
    }

    return result;
  }

  /**
   * The rows the table renders: filters, then the active column sort. Nulls
   * (e.g. "never validated") sort last in either direction.
   */
  get sortedConnections(): DevOpsConnection[] {
    const key = this.sortKey;
    const valueOf = (c: DevOpsConnection): SortValue => {
      switch (key) {
        case "name":
          return textValue(c.name);
        case "orgUrl":
          return textValue(c.orgUrl);
        case "defaultProject":
          return textValue(c.defaultProject);
        case "status":
          // The grid shows unset status as "unverified" — sort the same way.
          return textValue(c.status || "unverified");
        case "lastValidatedAt":
          return dateValue(c.lastValidatedAt);
      }
    };
    return sortRows(this.filteredConnections, valueOf, this.sortDir);
  }

  get totalCount(): number {
    return this.connections.length;
  }

  /** How many rows survive the header filters — what the table renders. */
  get filteredCount(): number {
    return this.filteredConnections.length;
  }

  /**
   * True when the server holds more connections than this page fetched — the
   * summary cards and the table are then BOTH short of reality. The fix for a
   * real overflow is paging, not a bigger `$limit`.
   */
  get isTruncated(): boolean {
    return this.serverTotal !== null && this.serverTotal > this.totalCount;
  }

  get validCount(): number {
    // The API writes "verified" on a successful validate; treat it as valid.
    return this.connections.filter((c) => isDevOpsConnectionValid(c.status)).length;
  }

  get unverifiedCount(): number {
    return this.connections.filter(
      (c) => !c.status || c.status === "unverified"
    ).length;
  }

  get invalidCount(): number {
    return this.connections.filter(
      (c) => c.status === "invalid" || c.status === "error"
    ).length;
  }

  // ---- Filter setters -----------------------------------------------------

  setSearchQuery(q: string) {
    this.searchQuery = q;
  }

  setStatusFilter(filter: DevOpsConnectionStatusFilter) {
    this.statusFilter = filter;
  }

  /** Column-header click: same column toggles direction, a new column starts
   *  in its natural direction (last-validated newest-first, text A→Z). */
  setSort(key: DevOpsConnectionSortKey) {
    const next = nextSort(this.sortKey, this.sortDir, key, DESC_FIRST);
    this.sortKey = next.key;
    this.sortDir = next.dir;
  }

  // ---- Loading ------------------------------------------------------------

  async loadConnections() {
    this.loading = true;
    this.error = null;
    try {
      // ServiceStore.find populates its entity cache, which `connections` reads.
      // A REAL cap: `devops-connections` is a paginated KnexService, so record
      // the envelope's `total` and let the view disclose any shortfall.
      // findOrThrow: the DevOps ServiceStores run without throwOnError, so a
      // failed find resolved null and the grid said "No Azure DevOps
      // connections yet." — the module's first-run screen — over a failure.
      const result = await findOrThrow(this.connectionsService, {
        query: { $limit: DEVOPS_CONNECTION_PAGE_SIZE, $sort: { name: 1 } },
      });
      const total = readFindTotal(result);
      runInAction(() => {
        this.serverTotal = total;
      });
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) this.error = loadErrorMessage(err, "Failed to load connections");
      });

      console.error("Failed to load DevOps connections:", err);
    } finally {
      runInAction(() => {
        this.loading = false;
      });
    }
  }

  /** Alias used by header refresh / post-write callbacks. */
  async reload() {
    await this.loadConnections();
  }

  /** Ensure the list is populated once (used by consumers like PipelinesBoard). */
  async ensureLoaded() {
    if (this.connections.length === 0 && !this.loading) {
      await this.loadConnections();
    }
  }

  // ---- Create / edit form -------------------------------------------------

  startCreate() {
    this.editingConnectionId = null;
    this.formError = null;
    this.lastTestResult = null;
    // pat starts empty and stays write-only.
    this.formData = {
      name: "",
      clientId: null,
      orgUrl: "https://dev.azure.com/",
      pat: "",
      defaultProject: "",
      scopes: "",
    };
  }

  startEdit(connection: DevOpsConnection) {
    this.editingConnectionId = connection.id;
    this.formError = null;
    this.lastTestResult = null;
    // IMPORTANT: never seed `pat` from a record — the API does not return it.
    // Leaving it blank means "keep the existing token"; a non-empty value on
    // save rotates it.
    this.formData = {
      name: connection.name,
      clientId: connection.clientId ?? null,
      orgUrl: connection.orgUrl,
      pat: "",
      defaultProject: connection.defaultProject || "",
      // API stores scopes as an array; present them as comma-separated text.
      scopes: Array.isArray(connection.scopes)
        ? connection.scopes.join(", ")
        : "",
    };
  }

  get isEditing(): boolean {
    return !!this.editingConnectionId;
  }

  setFormField(
    field: keyof DevOpsConnectionFormData,
    value: string | null
  ) {
    this.formData = { ...this.formData, [field]: value } as DevOpsConnectionFormData;
  }

  get isFormValid(): boolean {
    const hasBasics =
      !!this.formData.name?.trim() && !!this.formData.orgUrl?.trim();
    // On create a PAT is required; on edit a blank PAT means "unchanged".
    if (this.editingConnectionId) return hasBasics;
    return hasBasics && !!this.formData.pat?.trim();
  }

  /**
   * Persist the create/edit form. The PAT (when provided) is sent to the API
   * which encrypts it at rest; it is never echoed back. After saving, the
   * write-only `pat` field is cleared.
   */
  async saveConnection(): Promise<boolean> {
    if (!this.isFormValid) {
      this.formError = this.editingConnectionId
        ? "Name and Organization URL are required."
        : "Name, Organization URL, and a Personal Access Token are required.";
      return false;
    }
    this.saving = true;
    this.formError = null;
    try {
      const payload: Record<string, unknown> = {
        name: this.formData.name?.trim(),
        orgUrl: this.formData.orgUrl?.trim(),
        authType: "pat",
        clientId: this.formData.clientId || null,
      };
      // Only include the PAT when the user actually entered one. On edit an
      // empty value keeps the existing encrypted token untouched.
      if (this.formData.pat?.trim()) {
        payload.pat = this.formData.pat.trim();
      }
      if (this.formData.defaultProject?.trim()) {
        payload.defaultProject = this.formData.defaultProject.trim();
      }
      if (this.formData.scopes?.trim()) {
        // The API validates `scopes` as an array of strings (not a free-text
        // string), so split the comma/whitespace-separated input into an array.
        const scopesArr = this.formData.scopes
          .split(/[,\n]/)
          .map((s) => s.trim())
          .filter(Boolean);
        if (scopesArr.length) payload.scopes = scopesArr;
      }

      if (this.editingConnectionId) {
        await this.connectionsService?.patch(this.editingConnectionId, payload, THROW_ON_ERROR);
      } else {
        await this.connectionsService?.create(payload as never, THROW_ON_ERROR);
      }

      await this.reload();
      runInAction(() => {
        this.editingConnectionId = null;
        // Clear the write-only PAT so it never lingers in memory/state.
        this.formData = {};
      });
      return true;
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.formError =
            err instanceof Error ? err.message : "Failed to save connection";
        }
      });

      console.error("saveConnection failed:", err);
      return false;
    } finally {
      runInAction(() => {
        this.saving = false;
      });
    }
  }

  /** Dismiss the row-action error banner. */
  clearActionError() {
    this.actionError = null;
  }

  /**
   * Delete a connection (and, server-side, its encrypted PAT). Destructive and
   * irreversible — the caller MUST confirm first (the grid uses
   * `ConfirmingIconButton`). A failure is surfaced on `actionError`, never only
   * logged, so the operator knows the credential is still there.
   */
  async removeConnection(id: string) {
    this.actionError = null;
    try {
      await this.connectionsService?.remove(id, THROW_ON_ERROR);
      await this.reload();
    } catch (err) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.actionError =
            err instanceof Error ? err.message : "Failed to remove connection";
        }
      });

      console.error("Failed to remove connection:", err);
    }
  }

  /**
   * Trigger the API's `validate` custom action for a saved connection. The API
   * connects to Azure DevOps using the encrypted PAT and reports usability.
   * No secret is sent from the client.
   */
  async testConnection(id: string): Promise<void> {
    this.testingConnectionId = id;
    this.lastTestResult = null;
    try {
      // The API exposes connection validation as a custom action via `create`
      // (POST) with `{ action: 'validate', id }` — NOT patch. It returns the
      // updated connection record (top-level `status`/`lastValidatedAt`) with a
      // nested `validation` object. No secret is sent — only the action + id.
      const payload: DevOpsConnectionValidatePayload = { action: "validate", id };
      // The validate action POSTs `{ action, id }` to the service; the response
      // is the validation result/record, not a plain entity create.
      const response = (await this.connectionsService?.create(
        payload as never,
      )) as unknown as DevOpsConnectionValidateResponse | undefined;
      runInAction(() => {
        this.lastTestResult = {
          // The record status is the authoritative outcome ("verified"/"invalid").
          status: response?.validation?.status ?? response?.status ?? "error",
          message: response?.validation?.message,
          projectCount: response?.validation?.projectCount,
          lastValidatedAt: response?.lastValidatedAt ?? null,
        };
      });
      // Reload so the freshly-computed status/lastValidatedAt show in the list.
      await this.reload();
    } catch (err: unknown) {
      runInAction(() => {
        if (!isSessionEnded(err)) {
          this.lastTestResult = {
            status: "error",
            message:
              err instanceof Error ? err.message : "Connection test failed",
          };
        }
      });

      console.error("testConnection failed:", err);
    } finally {
      runInAction(() => {
        this.testingConnectionId = null;
      });
    }
  }

  /**
   * Back to the constructed state, so nothing of the person who signed out
   * is left for the next one. Called by RootStore.purgeUserData on sign-out.
   */
  reset() {
    this.loading = false;
    this.error = null;
    this.serverTotal = null;
    this.actionError = null;
    this.searchQuery = "";
    this.statusFilter = "all";
    this.sortKey = "name";
    this.sortDir = "asc";
    this.editingConnectionId = null;
    this.formData = {};
    this.saving = false;
    this.formError = null;
    this.testingConnectionId = null;
    this.lastTestResult = null;
  }
}

export default DevOpsConnectionsStore;
